// src/components/timeline/TimelineComments.tsx

"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import { PostComment } from "@/lib/timeline";
import {
  getPostCommentsAction,
  createPostCommentAction,
} from "@/app/[locale]/timeline/actions";
import { useAnonymousUser } from "@/hooks/useAnonymousUser";
import { useDateFormatter } from "@/hooks/useDateFormatter";

type Props = {
  projectId: number;
  postId: string;
  disabled?: boolean;
};

export default function TimelineComments({
  projectId,
  postId,
  disabled = false,
}: Props) {
  const t = useTranslations("timelineComments");
  const { userId: anonymousUserId } = useAnonymousUser();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [comments, setComments] = useState<PostComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isMountedRef = useRef(true);
  const { formatDateTime } = useDateFormatter();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || loaded) return;

    (async () => {
      setLoading(true);
      try {
        const res = await getPostCommentsAction(
          projectId,
          postId,
          1,
          20,
          anonymousUserId
        );
        if (isMountedRef.current) {
          setComments(res.results);
          setLoaded(true);
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error("Failed to load comments:", error);
        }
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    })();
  }, [open, loaded, projectId, postId, anonymousUserId]);

  async function submitComment() {
    const value = text.trim();
    if (!value || disabled) return;

    if (!anonymousUserId) {
      console.error("Cannot submit comment: No anonymous user ID");
      return;
    }

    try {
      setSubmitting(true);
      const created = await createPostCommentAction(
        projectId,
        postId,
        value,
        null,
        anonymousUserId
      );
      if (isMountedRef.current) {
        setComments((prev) => [...prev, created]);
        setText("");
        setOpen(true);
        setLoaded(true);
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error("Failed to submit comment:", error);
      }
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          cursor: "pointer",
          opacity: 0.7,
          fontSize: "0.88rem",
        }}
      >
        {open ? t("hideComments") : t("showComments")}
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {!disabled && (
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("writeCommentPlaceholder")}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "1px solid var(--card-border)",
                  borderRadius: 10,
                  padding: "0.7rem 0.9rem",
                  background: "transparent",
                  color: "inherit",
                }}
              />
              <button
                type="button"
                onClick={submitComment}
                disabled={submitting || !text.trim()}
                style={{
                  border: "1px solid var(--card-border)",
                  borderRadius: 10,
                  padding: "0.7rem 1rem",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                }}
              >
                {t("sendButton")}
              </button>
            </div>
          )}

          {disabled && (
            <div
              style={{ fontSize: "0.85rem", opacity: 0.55, marginBottom: 8 }}
            >
              {t("commentsDisabled")}
            </div>
          )}

          {loading ? (
            <div style={{ fontSize: "0.85rem", opacity: 0.6 }}>
              {t("loadingComments")}
            </div>
          ) : comments.length === 0 ? (
            <div style={{ fontSize: "0.85rem", opacity: 0.6 }}>
              {t("noComments")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: "0.8rem 0.9rem",
                    borderRadius: 12,
                    background:
                      "color-mix(in srgb, currentColor 4%, transparent)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.74rem",
                      opacity: 0.45,
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.74rem",
                        opacity: 0.45,
                        marginBottom: 4,
                      }}
                      suppressHydrationWarning
                    >
                      {formatDateTime(comment.created, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                  <div style={{ fontSize: "0.92rem" }}>
                    {comment.is_deleted ? t("deletedComment") : comment.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
